import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  TextField, Button, Box, Typography, Stack, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Paper
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFilter } from '../../layouts/dashboard/FilterProvider';
import WhatsappApiFunctions from '../../API/whatsapp';

const dropzoneStyle = {
  border: '2px dashed #cccccc',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  marginBottom: 2,
};

const URLs = process.env.REACT_APP_API_ENDPOINT;

const TemplateForm = ({ onClose }) => {
  const { setFilter, filter } = useFilter();
  const [loading, setLoading] = useState(false);
  const [eventList, setEventList] = useState([]);
  const [templateKey, setTemplateKey] = useState('')
  const [templateName, setTemplateName] = useState('');
  const [templateList, setTemplateList] = useState([]);

  const [customImage, setCustomImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [disableButton, setDisableButton] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({
    eventId: '',
    eventName: ''
  });

  // Theme and Font state
  const [themeList, setThemeList] = useState([]);
  const [fontList, setFontList] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedFont, setSelectedFont] = useState('');
  const [themePreviewOpen, setThemePreviewOpen] = useState(false);
  const [fontPreviewOpen, setFontPreviewOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    setCustomImage(acceptedFiles[0]);
    setPreviewImage(URL.createObjectURL(acceptedFiles[0]));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDisableButton(true);
    setLoading(true);
    const accessToken = localStorage.getItem('accessToken');

    const formData = new FormData();
    formData.append('TemplateKey', templateKey);
    formData.append("EventInfoKey", selectedEvent.eventId)
    
    // For custom_runner_image template, use theme, font, and custom image
    if (templateName === 'custom_runner_image') {
      if (selectedTheme) formData.append('ThemeId', selectedTheme);
      if (selectedFont) formData.append('FontId', selectedFont);
      if (customImage) formData.append('CustomImage', customImage);
    } else if (customImage) {
      formData.append('CustomImage', customImage);
    }

    try {
      const response = await fetch(`${URLs}Template/CreateEvent`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      console.log('API response:', data);
      // setFilter({ ...filter, "eventCount": filter.eventCount + 1 });
      toast.success('Event created sucessfully');
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong');
    } finally {
      setDisableButton(false);
      setLoading(false);
    }
  };

  const handleEventChange = (e) => {
    const selectedKey = e.target.value;
    const selected = eventList.find(event => event.EventInfoKey === Number(selectedKey));

    if (selected) {
      setSelectedEvent({
        eventId: selected.EventInfoKey,
        eventName: selected.EventName,
      });
    }
  };

  useEffect(() => {
    async function getTempleList() {
      try {
        const response = await WhatsappApiFunctions.getTempleList();
        const { Templates } = response;
        setTemplateList(Templates);
      } catch (error) {
        console.error('Error fetching message count:', error);
      }
    }
    getTempleList();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');

        const response = await fetch(`${URLs}Event/ListEventInfo`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();

        setEventList(data.Events);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchData();
  }, []);

  // Fetch themes and fonts when template is custom_runner_image
  useEffect(() => {
    const fetchThemeAndFonts = async () => {
      if (templateName === 'custom_runner_image') {
        const accessToken = localStorage.getItem('accessToken');
        
        // Fetch themes - try list-templates endpoint first, then fallback to GetTheme
        try {
          // Try list-templates endpoint (similar to list-fonts)
          const themeListResponse = await fetch(`${URLs}Font/list-templates`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (themeListResponse.ok) {
            const themeListData = await themeListResponse.json();
            if (themeListData && themeListData.Templates && Array.isArray(themeListData.Templates)) {
              setThemeList(themeListData.Templates);
            } else if (Array.isArray(themeListData)) {
              setThemeList(themeListData);
            } else {
              setThemeList([]);
            }
          } else {
            // Fallback to GetTheme endpoint
            const themeResponse = await fetch(`${URLs}Settings/GetTheme`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            if (themeResponse.ok) {
              const themeData = await themeResponse.json();
              setCurrentTheme(themeData);
              // If single theme object, wrap it in array
              if (themeData && Array.isArray(themeData)) {
                setThemeList(themeData);
              } else if (themeData && themeData.Themes && Array.isArray(themeData.Themes)) {
                setThemeList(themeData.Themes);
              } else {
                setThemeList(themeData ? [themeData] : []);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching theme:', err);
          // Try fallback to GetTheme
          try {
            const themeResponse = await fetch(`${URLs}Settings/GetTheme`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            if (themeResponse.ok) {
              const themeData = await themeResponse.json();
              setCurrentTheme(themeData);
              setThemeList(themeData ? [themeData] : []);
            }
          } catch (fallbackErr) {
            console.error('Error fetching theme fallback:', fallbackErr);
          }
        }

        // Fetch fonts
        try {
          const fontResponse = await fetch(`${URLs}Font/list-fonts`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (fontResponse.ok) {
            const fontData = await fontResponse.json();
            if (fontData && fontData.Fonts && Array.isArray(fontData.Fonts)) {
              setFontList(fontData.Fonts);
            } else if (Array.isArray(fontData)) {
              setFontList(fontData);
            } else {
              setFontList([]);
            }
          }
        } catch (err) {
          console.error('Error fetching fonts:', err);
        }
      } else {
        // Reset when not custom_runner_image
        setThemeList([]);
        setFontList([]);
        setSelectedTheme('');
        setSelectedFont('');
      }
    };

    fetchThemeAndFonts();
  }, [templateName]);

  return (
    <Stack gap={2} component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Autocomplete
        options={templateList}
        required
        disabled={disableButton}
        getOptionLabel={(option) => option?.TemplateName} // Display TemplateName in the input field
        renderInput={(params) => (
          <TextField
            {...params}
            label="Template"
            disabled={disableButton}
            required
            autoFocus
            variant="outlined"
            sx={{ width: '100%' }}
          />
        )}
        onChange={(event, value) => {
          if (value) {
            setTemplateKey(value.MessageTemplateKey); // Store the selected template in state
            setTemplateName(value.TemplateName || ''); // Store template name to check if it's custom_runner_image
          } else {
            setTemplateKey(null); // Reset if no value is selected
            setTemplateName(''); // Reset template name
          }
        }}
      />

      <FormControl fullWidth disabled={disableButton} required>
        <InputLabel id="event-select-label">Event Name</InputLabel>
        <Select
          labelId="event-select-label"
          value={selectedEvent.eventId || ''}
          label="Event Name"
          onChange={handleEventChange}
        >
          {eventList?.map((event) => (
            <MenuItem key={event?.EventInfoKey} value={event?.EventInfoKey || ''}>
              {event?.EventName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Show theme and font selectors for custom_runner_image template, otherwise show drag and drop */}
      {templateName === 'custom_runner_image' ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth disabled={disableButton} required>
              <InputLabel id="theme-select-label">Theme</InputLabel>
              <Select
                labelId="theme-select-label"
                value={selectedTheme || ''}
                label="Theme"
                onChange={(e) => setSelectedTheme(e.target.value)}
                renderValue={(value) => {
                  if (!value || !themeList?.length) return '';
                  const theme = themeList.find(t => 
                    String(t.id || t.theme_id || '') === String(value)
                  );
                  return theme ? (theme.name || theme.template_name || theme.theme_name || `Theme ${value}`) : '';
                }}
              >
                {themeList?.map((theme, index) => {
                  const themeValue = String(theme.id || theme.theme_id || index);
                  return (
                    <MenuItem key={theme.id || theme.theme_id || index} value={themeValue}>
                      {theme.name || theme.template_name || theme.theme_name || `Theme ${index + 1}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedTheme && (
              <IconButton
                onClick={() => setThemePreviewOpen(true)}
                disabled={disableButton}
                color="primary"
                sx={{ alignSelf: 'center' }}
              >
                <Visibility />
              </IconButton>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth disabled={disableButton} required>
              <InputLabel id="font-select-label">Font</InputLabel>
              <Select
                labelId="font-select-label"
                value={selectedFont || ''}
                label="Font"
                onChange={(e) => setSelectedFont(e.target.value)}
                renderValue={(value) => {
                  if (!value || !fontList?.length) return '';
                  const font = fontList.find(f => String(f.id) === String(value));
                  return font ? font.font_name : '';
                }}
              >
                {fontList?.map((font) => (
                  <MenuItem key={font.id} value={String(font.id)}>
                    {font.font_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedFont && (
              <IconButton
                onClick={() => setFontPreviewOpen(true)}
                disabled={disableButton}
                color="primary"
                sx={{ alignSelf: 'center' }}
              >
                <Visibility />
              </IconButton>
            )}
          </Stack>

          <Box {...getRootProps()} sx={dropzoneStyle}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>Drop the image here ...</Typography>
            ) : previewImage ? (
              <>
                <img
                  src={previewImage}
                  alt="preview"
                  style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '8px', maxHeight: '200px' }}
                />
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Selected file: {customImage.name}
                </Typography>
              </>
            ) : (
              <Typography>Drag 'n' drop an image here, or click to select from gallery</Typography>
            )}
          </Box>
        </>
      ) : (
        <Box {...getRootProps()} sx={dropzoneStyle}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography>Drop the image here ...</Typography>
          ) : previewImage ? (
            <>
              <img
                src={previewImage}
                alt="preview"
                style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '8px', maxHeight: '200px' }}
              />
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selected file: {customImage.name}
              </Typography>
            </>
          ) : (
            <Typography>Drag 'n' drop an image here, or click to select from gallery</Typography>
          )}
        </Box>
      )}

      {/* Theme Preview Dialog */}
      <Dialog 
        open={themePreviewOpen} 
        onClose={() => setThemePreviewOpen(false)} 
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '60vw',
            maxHeight: '60vh',
            width: 'auto',
            m: 2,
          }
        }}
      >
        <DialogTitle>Theme Preview</DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: 'calc(60vh - 120px)', overflow: 'auto' }}>
          {selectedTheme && themeList.find(t => (t.id || t.theme_id) === Number(selectedTheme)) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {(() => {
                const theme = themeList.find(t => (t.id || t.theme_id) === Number(selectedTheme));
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                      {theme.name || theme.template_name || theme.theme_name || 'Theme Preview'}
                    </Typography>
                    {theme.template_url || theme.template_file_url || theme.theme_url ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <img
                          src={theme.template_url || theme.template_file_url || theme.theme_url}
                          alt="Theme preview"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 'calc(60vh - 120px)', 
                            height: 'auto',
                            borderRadius: 8, 
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </Box>
                    ) : (
                      <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {JSON.stringify(theme, null, 2)}
                        </pre>
                      </Paper>
                    )}
                  </>
                );
              })()}
            </Box>
          ) : currentTheme ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                {currentTheme.name || currentTheme.template_name || 'Theme Preview'}
              </Typography>
              {currentTheme.template_url || currentTheme.template_file_url || currentTheme.theme_url ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <img
                    src={currentTheme.template_url || currentTheme.template_file_url || currentTheme.theme_url}
                    alt="Theme preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 'calc(60vh - 120px)', 
                      height: 'auto',
                      borderRadius: 8, 
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
              ) : (
                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                    {JSON.stringify(currentTheme, null, 2)}
                  </pre>
                </Paper>
              )}
            </Box>
          ) : (
            <Typography>No theme available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Font Preview Dialog */}
      <Dialog open={fontPreviewOpen} onClose={() => setFontPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Font Preview</DialogTitle>
        <DialogContent>
          {selectedFont && fontList.find(f => f.id === Number(selectedFont)) ? (
            <Box>
              {(() => {
                const font = fontList.find(f => f.id === Number(selectedFont));
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {font.font_name}
                    </Typography>
                    <style>
                      {`
                        @font-face {
                          font-family: 'PreviewFont-${font.id}';
                          src: url('${font.font_file_url}');
                        }
                      `}
                    </style>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '24px', mb: 1 }}>
                        The quick brown fox jumps over the lazy dog
                      </Typography>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '18px', mb: 1 }}>
                        ABCDEFGHIJKLMNOPQRSTUVWXYZ
                      </Typography>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '18px' }}>
                        abcdefghijklmnopqrstuvwxyz 0123456789
                      </Typography>
                    </Paper>
                  </>
                );
              })()}
            </Box>
          ) : (
            <Typography>No font selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFontPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Button
        disabled={
          loading || 
          disableButton || 
          !(templateKey && selectedEvent?.eventName) ||
          (templateName === 'custom_runner_image' && (!selectedTheme || !selectedFont || !customImage))
        }
        type="submit"
        variant="contained"
        fullWidth
        sx={{
          backgroundColor: '#9f1d21',
          color: '#ffffff', // white text
          '&:hover': {
            backgroundColor: '#e3272c', // darker hover shade
          },
          '&:disabled': {
            backgroundColor: '#f4aac9', // optional: light pink for disabled
            color: '#fff',
          },
        }}
      >
        {loading ? <div className="loader" /> : <>Submit</>}
      </Button>
    </Stack>
  );
};

export default TemplateForm;